/**
 * Page Tree Storage Service
 * Phase 2: Page Tree Integration + Unified State Model
 * 
 * Handles persistence of page tree structure. F 144: remote-backed, with
 * localStorage kept as an offline mirror.
 */

// F 144 — reads/writes go through RemoteState: an in-memory cache hydrated
// from the FCC state service and flushed back on a debounce. The localStorage
// mirror is kept in step, so behaviour is identical if the service is
// unreachable, and the signatures here stay SYNCHRONOUS.
import { RemoteState } from '../storage/RemoteState';
import { recordEntityChange, appendHistory } from './entityHistory';

const STORAGE_KEY = 'freshcards:pages';
const CURRENT_PAGE_KEY = 'freshcards:currentPageId';

/**
 * F 226 — TRASH. One page, as stored.
 *
 * `deletedAt` is additive: rows that predate it simply do not have it,
 * and a row without it is a live row. Nothing migrates.
 */
export interface StoredPage {
  id: string;
  title: string;
  parentId?: string;
  type?: string;
  deletedAt?: string;
  [k: string]: unknown;
}

/** F 226 — reads filter trashed rows by default. `includeDeleted` is the
 *  ONLY way to see them, and the Trash view is the only caller that sets
 *  it. Defaulting to exclusion means soft delete cannot make a deleted
 *  page surface in a surface that never asked for one. */
export function isTrashed(p: StoredPage): boolean {
  return typeof p?.deletedAt === 'string' && p.deletedAt.length > 0;
}

/** All stored pages, trashed included. The raw read — filtering is the
 *  caller's decision, and only two callers make it. */
export function readAllPages(): StoredPage[] {
  try {
    const data = RemoteState.get(STORAGE_KEY);
    if (data) return JSON.parse(data);
  } catch (error) {
    console.error('[PageTreeStorage] Failed to load pages:', error);
  }
  return [];
}

/** Live pages only. */
export function readLivePages(): StoredPage[] {
  return readAllPages().filter((p) => !isTrashed(p));
}

/** Trashed pages, newest first. F 226 — the Trash view's only read. */
export function readTrashedPages(): StoredPage[] {
  return readAllPages()
    .filter(isTrashed)
    .sort((a, b) => (String(a.deletedAt) < String(b.deletedAt) ? 1 : -1));
}

/**
 * F 226 — soft delete. Marks `deletedAt` and keeps the row.
 *
 * Idempotent: re-deleting an already-trashed page refreshes nothing and
 * changes nothing, so a double-click cannot move the timestamp and make
 * the page look freshly deleted.
 *
 * Returns false when the id is not in the tree, so a caller can tell
 * "trashed it" from "there was nothing there".
 */
export function softDeletePage(pageId: string, at?: string): boolean {
  const pages = readAllPages();
  const idx = pages.findIndex((p) => p.id === pageId);
  if (idx === -1) return false;
  if (isTrashed(pages[idx])) return true;
  pages[idx] = { ...pages[idx], deletedAt: at ?? new Date().toISOString() };
  savePages(pages);
  return true;
}

/** F 226 — restore. Clears `deletedAt`; the row was never removed. */
export function restorePage(pageId: string): boolean {
  const pages = readAllPages();
  const idx = pages.findIndex((p) => p.id === pageId);
  if (idx === -1) return false;
  if (!isTrashed(pages[idx])) return true;
  const { deletedAt: _drop, ...rest } = pages[idx];
  pages[idx] = rest as StoredPage;
  savePages(pages);
  return true;
}

/** F 226 — the operator path. Removes the row for good. */
export function hardDeletePage(pageId: string): boolean {
  const pages = readAllPages();
  const next = pages.filter((p) => p.id !== pageId);
  if (next.length === pages.length) return false;
  savePages(next);
  return true;
}

/**
 * Save pages to storage
 *
 * F 227 ITEM A — this is the page write path, and it is the ONLY place a
 * page is persisted, so it is the only place that needs to leave a history
 * record. Diffing here rather than at each of the six call sites is what
 * makes "every write is recorded" true by construction instead of by six
 * independent decisions to remember.
 *
 * The diff is per-entity, so saving ten pages records ten entities' worth
 * of change and NOT the nine that did not move. A no-op save appends
 * nothing at all.
 */
export function savePages(pages: Array<{
  id: string;
  title: string;
  parentId?: string;
  type?: string;
}>): void {
  try {
    // F 227 — capture the previous state BEFORE the write. `readAllPages`
    // parses the stored JSON, so this is the persisted truth, not a
    // closure variable that could be stale.
    const before = readAllPages();
    RemoteState.set(STORAGE_KEY, JSON.stringify(pages));

    const after = pages as unknown as Array<Record<string, unknown>>;
    const prev = new Map<string, Record<string, unknown>>(
      before.map((p) => [p.id, p as unknown as Record<string, unknown>]),
    );
    for (const page of after) {
      recordEntityChange(prev.get(String(page.id)), page);
    }
    // A row that disappeared is a change too. It is recorded as a
    // hard-delete delta rather than silently vanishing from the log.
    for (const old of before) {
      if (after.some((p) => p.id === old.id)) continue;
      appendHistory(old.id, { _deleted: { before: old.title ?? null, after: null } });
    }
  } catch (error) {
    console.error('[PageTreeStorage] Failed to save pages:', error);
  }
}

/**
 * Load pages from storage
 *
 * F 226 — returns LIVE pages. Trashed rows are filtered out here, so
 * every existing consumer gets the old behaviour without being edited.
 * `readTrashedPages` is the only way to see them.
 */
export function loadPages(): Array<{
  id: string;
  title: string;
  parentId?: string;
  type?: string;
}> {
  return readLivePages() as Array<{
    id: string;
    title: string;
    parentId?: string;
    type?: string;
  }>;
}

/**
 * Save current page ID to storage
 */
export function saveCurrentPageId(pageId: string | null): void {
  try {
    if (pageId) {
      console.log('[Storage] Saving currentPageId:', pageId);
      RemoteState.set(CURRENT_PAGE_KEY, pageId);
    } else {
      console.log('[Storage] Clearing currentPageId');
      RemoteState.remove(CURRENT_PAGE_KEY);
    }
  } catch (error) {
    console.error('[PageTreeStorage] Failed to save current page:', error);
  }
}

/**
 * Load current page ID from storage
 */
export function loadCurrentPageId(): string | null {
  try {
    const result = RemoteState.get(CURRENT_PAGE_KEY);
    console.log('[Storage] Loading currentPageId:', result);
    return result;
  } catch (error) {
    console.error('[PageTreeStorage] Failed to load current page:', error);
    return null;
  }
}

/**
 * Clear all stored pages
 */
export function clearPages(): void {
  try {
    RemoteState.remove(STORAGE_KEY);
    RemoteState.remove(CURRENT_PAGE_KEY);
  } catch (error) {
    console.error('[PageTreeStorage] Failed to clear pages:', error);
  }
}

/**
 * Check if storage has pages
 */
export function hasStoredPages(): boolean {
  return RemoteState.get(STORAGE_KEY) !== null;
}

export default {
  savePages,
  loadPages,
  saveCurrentPageId,
  loadCurrentPageId,
  clearPages,
  hasStoredPages,
  // F 226 — TRASH
  isTrashed,
  readAllPages,
  readLivePages,
  readTrashedPages,
  softDeletePage,
  restorePage,
  hardDeletePage,
};